/**
 * Landing page / editor. Contribution data is fetched once per username from our API; every
 * customization, graph type and preset range after that re-renders locally with the same
 * renderer the API uses. Only a custom range older than the last year triggers another fetch.
 */
import { ERROR_MESSAGES, GraphErrorCode } from '../src/github/errors';
import { ContributionCalendar, DateWindow } from '../src/github/types';
import { parseUsername } from '../src/github/username';
import { layoutCombo } from '../src/graph/combo/layout';
import { layoutLine } from '../src/graph/line/layout';
import {
    GraphOptions,
    GraphType,
    RangeName,
    TYPE_DEFAULTS,
    defaultOptions,
    normalizeWindow,
    parseGraphOptions,
    serializeGraphOptions,
} from '../src/graph/options';
import { GRAPH_DEFINITIONS } from '../src/graph/registry';
import { GraphData, prepareData, renderGraph } from '../src/graph/render';
import { addDays, formatDate, toIsoDate } from '../src/graph/shared/dates';
import { formatNumber } from '../src/graph/shared/escape';
import { coversWindow } from '../src/graph/shared/range';
import { computeStats } from '../src/graph/shared/stats';
import { themeColors } from '../src/graph/themes';
import { trackEvent } from './analytics';
import { fetchContributions } from './api';
import { ControlChange, createControls } from './controls';
import { byId, copyText, debounce, downloadFile, safeStorage, showToast } from './dom';
import { EmbedFormat, buildGraphUrl, buildSnippet } from './embed';
import { sampleData } from './sample';
import { HoverModel, comboHoverModel, createChartTooltip, lineHoverModel } from './tooltip';

const ERROR_TITLES: Record<GraphErrorCode, string> = {
    USERNAME_REQUIRED: 'Username required',
    INVALID_USERNAME: 'Invalid username',
    NOT_FOUND: 'User not found',
    RATE_LIMITED: 'Rate limit reached',
    UNAVAILABLE: 'GitHub unavailable',
    INTERNAL: 'Graph generation error',
};
const RETRYABLE: GraphErrorCode[] = ['RATE_LIMITED', 'UNAVAILABLE', 'INTERNAL'];

interface State {
    options: GraphOptions;
    /** GitHub's rolling last-year calendar for the loaded user. */
    calendar: ContributionCalendar | null;
    /** Extra calendars for custom ranges outside the last year, keyed by `from|to`. */
    windows: Map<string, ContributionCalendar>;
    loading: boolean;
    error: GraphErrorCode | null;
    format: EmbedFormat;
}

const state: State = {
    options: parseGraphOptions(Object.fromEntries(new URLSearchParams(location.search))),
    calendar: null,
    windows: new Map(),
    loading: false,
    error: null,
    format: 'markdown',
};

const sample = sampleData();
let lastSvg = '';
let controller: AbortController | undefined;
/** `username|type` pairs already reported as previewed, so restyling doesn't re-count. */
const previewed = new Set<string>();

const el = {
    form: byId<HTMLFormElement>('username-form'),
    input: byId<HTMLInputElement>('username-input'),
    inputShell: byId('input-shell'),
    generate: byId<HTMLButtonElement>('generate-button'),
    formMessage: byId('form-message'),
    stage: byId('stage'),
    preview: byId('preview'),
    overlay: byId('stage-overlay'),
    stateBox: byId('state'),
    stateTitle: byId('state-title'),
    stateMessage: byId('state-message'),
    retry: byId<HTMLButtonElement>('retry-button'),
    badge: byId('preview-badge'),
    stats: byId('stats'),
    snippet: byId('snippet'),
    snippetCode: byId('snippet-code'),
    copy: byId<HTMLButtonElement>('copy-button'),
    download: byId<HTMLButtonElement>('download-button'),
    reset: byId<HTMLButtonElement>('reset-button'),
    pageTheme: byId<HTMLButtonElement>('page-theme-toggle'),
};

const tooltip = createChartTooltip(el.stage, el.preview);

/** Hover data for chart-style graphs (the heatmap relies on its native SVG tooltips). */
const hoverModel = (data: GraphData, options: GraphOptions): HoverModel | null => {
    const prepared = prepareData(data, options);
    if (options.type === 'line') return lineHoverModel(layoutLine(prepared, options));
    if (options.type === 'combo') return comboHoverModel(layoutCombo(prepared, options));
    return null;
};

/* ---------------- Data selection ---------------- */

const windowKey = (window: DateWindow) => `${window.from}|${window.to}`;

/** True when the custom range starts before the loaded last-year calendar. */
const needsWindow = (): boolean =>
    Boolean(state.calendar) &&
    state.options.range === 'custom' &&
    !coversWindow(state.calendar!.days, state.options.from);

const activeCalendar = (): ContributionCalendar | null => {
    if (!state.calendar) return null;
    return needsWindow() ? (state.windows.get(windowKey(state.options)) ?? null) : state.calendar;
};

const toGraphData = (calendar: ContributionCalendar): GraphData => ({
    username: calendar.username,
    name: calendar.name,
    total: calendar.total,
    days: calendar.days,
});

const graphData = (): GraphData => {
    const calendar = activeCalendar();
    if (calendar) return toGraphData(calendar);
    // A window is still loading: keep the user's identity, show no days yet.
    if (state.calendar) return { ...toGraphData(state.calendar), days: [], total: 0 };
    return sample;
};

/** Options as they will appear in the shared URL (username = the loaded user). */
const effectiveOptions = (): GraphOptions => ({
    ...state.options,
    username: state.calendar?.username ?? state.options.username,
});

/* ---------------- Rendering ---------------- */

const renderPreview = () => {
    const options = effectiveOptions();
    try {
        const data = graphData();
        lastSvg = renderGraph(data, options);
        el.preview.innerHTML = lastSvg;
        tooltip.update(hoverModel(data, options), `#${options.colors.point}`);
    } catch (error) {
        console.error(error);
        lastSvg = '';
        el.preview.innerHTML = '';
        tooltip.update(null, '');
        state.error = 'INTERNAL';
    }
};

const renderOverlay = () => {
    el.stage.classList.toggle('is-busy', state.loading || Boolean(state.error));
    if (state.loading) {
        el.overlay.hidden = false;
        el.stateBox.classList.remove('is-error');
        el.stateTitle.textContent = 'Fetching contributions…';
        el.stateMessage.textContent = `Loading public activity for ${state.options.username}.`;
        el.retry.hidden = true;
    } else if (state.error) {
        el.overlay.hidden = false;
        el.stateBox.classList.add('is-error');
        el.stateTitle.textContent = ERROR_TITLES[state.error];
        el.stateMessage.textContent = ERROR_MESSAGES[state.error];
        el.retry.hidden = !RETRYABLE.includes(state.error) || !state.options.username;
    } else {
        el.overlay.hidden = true;
    }
};

const renderStats = () => {
    const calendar = activeCalendar();
    if (!calendar) {
        el.stats.hidden = true;
        return;
    }
    // Stats follow the selected date range.
    const stats = computeStats(prepareData(toGraphData(calendar), state.options).days);
    const values: Record<string, string> = {
        total: formatNumber(stats.total),
        activeDays: formatNumber(stats.activeDays),
        longestStreak: `${stats.longestStreak} day${stats.longestStreak === 1 ? '' : 's'}`,
        bestDay: stats.bestDay
            ? `${stats.bestDay.count} · ${formatDate(stats.bestDay.date).slice(5, -6)}`
            : '—',
    };
    for (const dd of el.stats.querySelectorAll<HTMLElement>('[data-stat]'))
        dd.textContent = values[dd.dataset.stat ?? ''] ?? '';
    el.stats.hidden = false;
};

const renderEmbed = () => {
    const ready = Boolean(activeCalendar()) && !state.error && Boolean(lastSvg);
    el.snippet.classList.toggle('is-empty', !ready);
    el.copy.disabled = !ready;
    el.download.disabled = !ready;
    el.snippetCode.textContent = ready
        ? buildSnippet(
              state.format,
              buildGraphUrl(location.origin, effectiveOptions()),
              GRAPH_DEFINITIONS[state.options.type].altText,
          )
        : 'Generate a graph to get your embed code.';
    el.badge.textContent = state.calendar ? `@${state.calendar.username}` : 'Sample data';
    el.badge.classList.toggle('is-live', Boolean(state.calendar));
};

const syncUrl = debounce(() => {
    const params = serializeGraphOptions(effectiveOptions()).toString();
    history.replaceState(null, '', params ? `?${params}` : location.pathname);
}, 250);

let controls: ReturnType<typeof createControls>;

/** Reports the first successful preview per user and graph type (not every re-render). */
const trackPreview = () => {
    const calendar = activeCalendar();
    if (!calendar || state.error || state.loading || !lastSvg) return;
    const key = `${calendar.username.toLowerCase()}|${state.options.type}`;
    if (previewed.has(key)) return;
    previewed.add(key);
    trackEvent('graph_previewed', {
        graphType: state.options.type,
        dateRange: state.options.range,
    });
};

const render = () => {
    renderPreview();
    trackPreview();
    renderOverlay();
    renderStats();
    renderEmbed();
    controls.sync(state.options);
    syncUrl();
};

/* ---------------- Fetching ---------------- */

const setFormMessage = (message: string, isError = false) => {
    el.formMessage.textContent = message;
    el.formMessage.classList.toggle('is-error', isError);
    el.inputShell.classList.toggle('is-invalid', isError);
    el.input.setAttribute('aria-invalid', String(isError));
};

const setLoading = (loading: boolean) => {
    state.loading = loading;
    el.generate.classList.toggle('is-loading', loading);
    el.generate.disabled = loading;
    el.generate.querySelector('.button-label')!.textContent = loading
        ? 'Generating…'
        : 'Generate graph';
};

/** Fetches the custom window if the current range needs it and it isn't loaded yet. */
const ensureWindow = async () => {
    if (!needsWindow() || !state.calendar) return;
    const window = { from: state.options.from, to: state.options.to };
    if (state.windows.has(windowKey(window))) return;

    controller?.abort();
    controller = new AbortController();
    state.error = null;
    setLoading(true);
    render();

    const result = await fetchContributions(state.calendar.username, controller.signal, window);
    if (!result.ok && result.aborted) return;
    setLoading(false);
    if (result.ok) state.windows.set(windowKey(window), result.calendar);
    else state.error = result.code;
    render();
};

const generate = async (rawUsername: string) => {
    const parsed = parseUsername(rawUsername);
    if (!parsed.ok) {
        setFormMessage(
            ERROR_MESSAGES[parsed.reason === 'empty' ? 'USERNAME_REQUIRED' : 'INVALID_USERNAME'],
            true,
        );
        el.input.focus();
        return;
    }
    const { username } = parsed;
    el.input.value = username;
    setFormMessage('');

    controller?.abort();
    controller = new AbortController();
    state.options.username = username;
    state.error = null;
    setLoading(true);
    render();

    const result = await fetchContributions(username, controller.signal, undefined, {
        type: state.options.type,
        range: state.options.range,
    });
    if (!result.ok && result.aborted) return; // superseded by a newer request

    setLoading(false);
    state.windows.clear();
    if (result.ok) {
        state.calendar = result.calendar;
        state.error = null;
        setFormMessage(
            `Loaded ${formatNumber(result.calendar.total)} contributions for ${result.calendar.username}.`,
        );
    } else {
        state.calendar = null;
        state.error = result.code;
        if (result.code === 'NOT_FOUND' || result.code === 'INVALID_USERNAME')
            setFormMessage(ERROR_MESSAGES[result.code], true);
    }
    render();
    void ensureWindow();
};

/* ---------------- Customization ---------------- */

/** Switches graph type, moving settings the user never changed to the new type's defaults. */
const switchType = (type: GraphType) => {
    const { options } = state;
    const before = defaultOptions(options.type);
    const after = defaultOptions(type);
    if (options.theme === TYPE_DEFAULTS[options.type].theme) {
        options.theme = TYPE_DEFAULTS[type].theme;
        options.colors = themeColors(options.theme);
    }
    // Each type has a range that suits it (a 7-day heatmap is a single column), so switch to
    // the new type's default unless the user picked explicit dates.
    if (options.range !== 'custom') options.range = after.range;
    // Other per-type defaults (padding, grid style, points…) follow unless the user changed them.
    const keys = new Set([
        ...Object.keys(TYPE_DEFAULTS[options.type]),
        ...Object.keys(TYPE_DEFAULTS[type]),
    ]);
    for (const key of keys as Set<keyof GraphOptions>) {
        if (key === 'theme' || key === 'range') continue;
        if (options[key] === before[key])
            (options as unknown as Record<string, unknown>)[key] = after[key];
    }
    options.type = type;
};

/** When switching to "custom", start from whatever range is currently displayed. */
const startCustomRange = () => {
    const shown = prepareData(graphData(), state.options).days;
    const today = toIsoDate(Date.now());
    const to = shown.length ? shown[shown.length - 1].date : today;
    const from = shown.length ? shown[0].date : addDays(today, -29);
    Object.assign(state.options, { range: 'custom' as RangeName, from, to });
};

const applyChange = (change: ControlChange) => {
    const { options } = state;
    switch (change.kind) {
        case 'type':
            switchType(change.type);
            trackEvent('graph_type_selected', { graphType: change.type });
            break;
        case 'theme':
            options.theme = change.theme;
            // Switching to "custom" keeps the current colors as the starting point.
            if (change.theme !== 'custom') options.colors = themeColors(change.theme);
            break;
        case 'color':
            options.colors = { ...options.colors, [change.key]: change.value };
            options.theme = 'custom';
            break;
        case 'range':
            if (change.range === 'custom') startCustomRange();
            else Object.assign(options, { range: change.range, from: '', to: '' });
            break;
        case 'window': {
            const window = normalizeWindow(change.from, change.to);
            if (window) Object.assign(options, { range: 'custom', ...window });
            break;
        }
        case 'option':
            change.apply(options);
            break;
    }
    if (state.error === 'INTERNAL') state.error = null;
    render();
    if (change.kind === 'range' || change.kind === 'window') void ensureWindow();
};

/* ---------------- Wiring ---------------- */

const initTabs = () => {
    const tabs = [...document.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
    const select = (tab: HTMLButtonElement) => {
        for (const other of tabs) {
            const selected = other === tab;
            other.setAttribute('aria-selected', String(selected));
            other.tabIndex = selected ? 0 : -1;
        }
        el.snippet.setAttribute('aria-labelledby', tab.id);
        state.format = tab.dataset.format as EmbedFormat;
        renderEmbed();
    };
    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
            const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
            if (!delta) return;
            const next = tabs[(index + delta + tabs.length) % tabs.length];
            next.focus();
            select(next);
        });
    });
};

const initCanvasSwitch = () => {
    const inputs = [...document.querySelectorAll<HTMLInputElement>('input[name="canvas"]')];
    const initial = safeStorage.get('gag-canvas') === 'dark' ? 'dark' : 'light';
    for (const input of inputs) {
        input.checked = input.value === initial;
        input.addEventListener('change', () => {
            el.stage.dataset.canvas = input.value;
            safeStorage.set('gag-canvas', input.value);
        });
    }
    el.stage.dataset.canvas = initial;
};

const initPageTheme = () => {
    const root = document.documentElement;
    el.pageTheme.addEventListener('click', () => {
        const current =
            root.dataset.theme ??
            (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        const next = current === 'dark' ? 'light' : 'dark';
        root.dataset.theme = next;
        safeStorage.set('gag-page-theme', next);
    });
};

const init = () => {
    controls = createControls(applyChange);
    initTabs();
    initCanvasSwitch();
    initPageTheme();

    el.form.addEventListener('submit', (event) => {
        event.preventDefault();
        void generate(el.input.value);
    });
    el.input.addEventListener('input', () => {
        if (el.inputShell.classList.contains('is-invalid')) setFormMessage('');
    });
    el.retry.addEventListener('click', () => void generate(state.options.username));

    el.copy.addEventListener('click', async () => {
        const ok = await copyText(el.snippetCode.textContent ?? '');
        const label = { markdown: 'Markdown', html: 'HTML', url: 'Graph URL' }[state.format];
        showToast(
            ok
                ? `${label} copied to clipboard`
                : 'Copy failed. Select the text and copy it manually.',
        );
        if (ok) {
            if (state.format === 'url')
                trackEvent('graph_url_copied', { graphType: state.options.type });
            else
                trackEvent('embed_code_copied', {
                    graphType: state.options.type,
                    format: state.format,
                });
            el.copy.textContent = 'Copied';
            setTimeout(() => (el.copy.textContent = 'Copy'), 1500);
        }
    });

    el.download.addEventListener('click', () => {
        if (!lastSvg || !state.calendar) return;
        downloadFile(
            `${state.calendar.username}-${state.options.type}-graph.svg`,
            lastSvg,
            'image/svg+xml',
        );
        trackEvent('graph_downloaded', {
            graphType: state.options.type,
            dateRange: state.options.range,
        });
    });

    el.reset.addEventListener('click', () => {
        const { username, type } = state.options;
        state.options = { ...defaultOptions(type), username };
        if (state.error === 'INTERNAL') state.error = null;
        render();
    });

    render();

    // Deep link: /?username=octocat&type=line&theme=dark loads straight away.
    if (state.options.username) {
        el.input.value = state.options.username;
        void generate(state.options.username);
    }
};

init();
